// Categories Admin — Phase 15.2 PR 4.
//
// Admin-only surface for managing the canonical merch'd category tree
// + reviewing pending supplier→canonical category mappings (curator queue).
//
// Layout:
//   - Top section: pending mappings curator queue (renders only when rows
//     exist). Each row shows the raw supplier category string, an inline
//     <select> of canonical categories, and an Assign button.
//   - Main section: flat-with-depth tree of canonical categories. Each
//     row supports inline rename / sort_order edit / deactivate. Add-child
//     and add-root buttons surface inline forms following the same edit
//     pattern as OrderDetailPage.
//
// Pattern mirrors OrderDetailPage / ClientDetailPage — TanStack Query for
// fetch, useMutation with optimistic updates, plain useState for forms,
// Escape to cancel. No new UI libraries.
//
// Non-admin users see a 401-style empty state (the route is also gated
// server-side by requireRole('admin'); the page just renders an empty
// state in that case).

import { useEffect, useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError, api } from "@/lib/api";
import { cn } from "@/lib/utils";

// ─── Types (shape mirrors backend response shapes verbatim) ──────────────

interface CategoryAdminRow {
  id: string;
  slug: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  productCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

interface PendingMapping {
  id: string;
  supplierSlug: string;
  supplierCategoryName: string;
  merchdCategoryId: string | null;
  merchdCategorySlug: string | null;
  merchdCategoryName: string | null;
  confidence: "manual" | "high" | "medium" | "low" | "pending";
  affectedProductCount: number;
  reviewedBy: string | null;
  reviewedAt: string | null;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface CategoriesListResponse {
  rows: CategoryAdminRow[];
}

interface PendingMappingsResponse {
  rows: PendingMapping[];
}

// ─── Tree-build helper ────────────────────────────────────────────────────

interface TreeRow extends CategoryAdminRow {
  depth: number;
}

/** Convert flat parent-pointer rows to a depth-flagged DFS list ordered by
 *  sortOrder then name within siblings. Orphans (parentId not in the set)
 *  surface as roots — defensive against inactive parents with active
 *  children. */
function flattenTree(rows: CategoryAdminRow[]): TreeRow[] {
  const byParent = new Map<string | null, CategoryAdminRow[]>();
  const ids = new Set(rows.map((r) => r.id));
  for (const r of rows) {
    const key = r.parentId && ids.has(r.parentId) ? r.parentId : null;
    let bucket = byParent.get(key);
    if (!bucket) {
      bucket = [];
      byParent.set(key, bucket);
    }
    bucket.push(r);
  }
  for (const bucket of byParent.values()) {
    bucket.sort(
      (a, b) =>
        a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
    );
  }
  const out: TreeRow[] = [];
  function walk(parentId: string | null, depth: number): void {
    const bucket = byParent.get(parentId) ?? [];
    for (const r of bucket) {
      out.push({ ...r, depth });
      walk(r.id, depth + 1);
    }
  }
  walk(null, 0);
  return out;
}

// ─── Page ─────────────────────────────────────────────────────────────────

export function CategoriesAdminPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("admin") ?? false;

  const queryClient = useQueryClient();
  const categoriesKey = useMemo(() => ["admin", "categories"] as const, []);
  const pendingKey = useMemo(
    () => ["admin", "category-mappings", "pending"] as const,
    [],
  );

  const categoriesQ = useQuery({
    queryKey: categoriesKey,
    queryFn: () => api<CategoriesListResponse>("/admin/categories"),
    enabled: isAdmin,
  });

  const pendingQ = useQuery({
    queryKey: pendingKey,
    queryFn: () =>
      api<PendingMappingsResponse>("/admin/category-mappings/pending"),
    enabled: isAdmin,
  });
  const tree = useMemo(
    () => flattenTree(categoriesQ.data?.rows ?? []),
    [categoriesQ.data],
  );

  if (!isAdmin) {
    return (
      <div className="mx-auto w-full">
        <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Categories
        </h1>
        <p className="mt-3 text-sm text-brown-300">
          This admin surface is only visible to users with the admin role.
        </p>
      </div>
    );
  }

  const pendingRows = pendingQ.data?.rows ?? [];

  return (
    <div className="mx-auto w-full space-y-10">
      {/* Header */}
      <div>
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Admin
        </p>
        <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Categories
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-brown-400">
          Tag every supplier product with an Other category so customers
          can find it. Storefront browse menus build directly from these
          picks — anything left in the queue below doesn't show up until it
          has a home.
        </p>
      </div>

      {/* Pending mappings curator queue */}
      <CuratorQueueSection
        rows={pendingRows}
        categories={categoriesQ.data?.rows ?? []}
        loading={pendingQ.isLoading}
        error={pendingQ.isError}
        onAssigned={() => {
          void queryClient.invalidateQueries({ queryKey: pendingKey });
          void queryClient.invalidateQueries({ queryKey: categoriesKey });
        }}
      />

      {/* Canonical category tree */}
      <CategoryTreeSection
        tree={tree}
        loading={categoriesQ.isLoading}
        error={categoriesQ.isError}
        onMutated={() => {
          void queryClient.invalidateQueries({ queryKey: categoriesKey });
        }}
      />
    </div>
  );
}

// ─── Curator queue ────────────────────────────────────────────────────────

function CuratorQueueSection(props: {
  rows: PendingMapping[];
  categories: CategoryAdminRow[];
  loading: boolean;
  error: boolean;
  onAssigned: () => void;
}) {
  const { rows, categories, loading, error, onAssigned } = props;

  if (loading) {
    return (
      <section>
        <SectionTitle
          eyebrow="Curator queue"
          title="Pending category mappings"
        />
        <p className="mt-3 text-sm text-brown-300">Loading…</p>
      </section>
    );
  }
  if (error) {
    return (
      <section>
        <SectionTitle
          eyebrow="Curator queue"
          title="Pending category mappings"
        />
        <p className="mt-3 text-sm text-rust-600">
          Could not load the curator queue.
        </p>
      </section>
    );
  }
  if (rows.length === 0) {
    return (
      <section>
        <SectionTitle
          eyebrow="Curator queue"
          title="Pending category mappings"
        />
        <p className="mt-3 text-sm text-brown-300">
          No pending mappings — all supplier categories are assigned.
        </p>
      </section>
    );
  }

  // Pick only active categories as assignment targets.
  const activeCategories = categories.filter((c) => c.isActive);

  return (
    <section>
      <SectionTitle
        eyebrow="Curator queue"
        title={`Pending category mappings (${rows.length})`}
      />
      <div className="mt-4 overflow-hidden rounded-lg border border-brown-100 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
            <tr>
              <th className="px-4 py-3 font-semibold text-brown-300">Supplier</th>
              <th className="px-4 py-3 font-semibold text-brown-300">Raw category</th>
              <th className="px-4 py-3 font-semibold text-brown-300">Currently</th>
              <th className="px-4 py-3 font-semibold text-brown-300">
                Assign to
              </th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <PendingMappingRow
                key={row.id}
                row={row}
                categories={activeCategories}
                onAssigned={onAssigned}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PendingMappingRow(props: {
  row: PendingMapping;
  categories: CategoryAdminRow[];
  onAssigned: () => void;
}) {
  const { row, categories, onAssigned } = props;
  const [target, setTarget] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const assign = useMutation({
    mutationFn: async (merchdCategoryId: string) => {
      return api<{ mapping: PendingMapping }>(
        `/admin/category-mappings/${row.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ merchd_category_id: merchdCategoryId }),
        },
      );
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; message?: string } | null;
        setError(body?.message ?? body?.error ?? "Assign failed.");
      } else {
        setError("Assign failed. Try again or check your connection.");
      }
    },
    onSuccess: () => {
      setError(null);
      setTarget("");
      onAssigned();
    },
  });

  return (
    <tr className="border-b border-brown-50 last:border-0">
      <td className="px-4 py-3 align-top">
        <span className="text-xs font-semibold uppercase tracking-wide text-brown-400">
          {row.supplierSlug}
        </span>
      </td>
      <td className="px-4 py-3 align-top">
        <span className="font-semibold text-brown-500">
          {row.supplierCategoryName}
        </span>
      </td>
      <td className="px-4 py-3 align-top text-brown-400">
        {row.merchdCategoryName ?? (
          <span className="italic text-brown-300">unmapped</span>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="w-full rounded-md border border-brown-100 bg-white px-2 py-1.5 text-sm text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200"
        >
          <option value="">Select a category…</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.slug})
            </option>
          ))}
        </select>
        {error && (
          <p className="mt-1 text-xs text-rust-600">{error}</p>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <Button
          size="sm"
          disabled={target === "" || assign.isPending}
          onClick={() => target && assign.mutate(target)}
        >
          {assign.isPending ? "Assigning…" : "Assign"}
        </Button>
      </td>
    </tr>
  );
}

// ─── Category tree ────────────────────────────────────────────────────────

function CategoryTreeSection(props: {
  tree: TreeRow[];
  loading: boolean;
  error: boolean;
  onMutated: () => void;
}) {
  const { tree, loading, error, onMutated } = props;
  const [addingRoot, setAddingRoot] = useState(false);
  const [addingChildOf, setAddingChildOf] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  if (loading) {
    return (
      <section>
        <SectionTitle eyebrow="Tree" title="Canonical categories" />
        <p className="mt-3 text-sm text-brown-300">Loading…</p>
      </section>
    );
  }
  if (error) {
    return (
      <section>
        <SectionTitle eyebrow="Tree" title="Canonical categories" />
        <p className="mt-3 text-sm text-rust-600">
          Could not load the category tree.
        </p>
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-end justify-between gap-4">
        <SectionTitle
          eyebrow="Tree"
          title={`Canonical categories (${tree.filter((r) => r.isActive).length} active)`}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setAddingRoot(true);
            setEditingId(null);
            setAddingChildOf(null);
          }}
        >
          <Plus className="mr-1 h-4 w-4" /> Add root category
        </Button>
      </div>

      {addingRoot && (
        <div className="mt-4">
          <CategoryForm
            mode="create"
            parentId={null}
            onCancel={() => setAddingRoot(false)}
            onSaved={() => {
              setAddingRoot(false);
              onMutated();
            }}
          />
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-lg border border-brown-100 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
            <tr>
              <th className="px-4 py-3 font-semibold text-brown-300">Name</th>
              <th className="px-4 py-3 font-semibold text-brown-300">Slug</th>
              <th className="px-4 py-3 font-semibold text-brown-300">Sort</th>
              <th className="px-4 py-3 font-semibold text-brown-300">Products</th>
              <th className="px-4 py-3 font-semibold text-brown-300">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {tree.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-brown-300">
                  No categories yet — add a root category to get started.
                </td>
              </tr>
            )}
            {tree.map((row) =>
              editingId === row.id ? (
                <tr key={row.id} className="border-b border-brown-50 last:border-0">
                  <td colSpan={6} className="bg-peach-50/40 p-4">
                    <CategoryForm
                      mode="edit"
                      row={row}
                      onCancel={() => setEditingId(null)}
                      onSaved={() => {
                        setEditingId(null);
                        onMutated();
                      }}
                    />
                  </td>
                </tr>
              ) : (
                <CategoryTreeRow
                  key={row.id}
                  row={row}
                  isAddingChild={addingChildOf === row.id}
                  onEdit={() => {
                    setEditingId(row.id);
                    setAddingRoot(false);
                    setAddingChildOf(null);
                  }}
                  onAddChild={() => {
                    setAddingChildOf(row.id);
                    setAddingRoot(false);
                    setEditingId(null);
                  }}
                  onCancelAddChild={() => setAddingChildOf(null)}
                  onChildSaved={() => {
                    setAddingChildOf(null);
                    onMutated();
                  }}
                />
              ),
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CategoryTreeRow(props: {
  row: TreeRow;
  isAddingChild: boolean;
  onEdit: () => void;
  onAddChild: () => void;
  onCancelAddChild: () => void;
  onChildSaved: () => void;
}) {
  const { row, isAddingChild, onEdit, onAddChild, onCancelAddChild, onChildSaved } = props;
  const indent = row.depth * 20;
  return (
    <>
      <tr className={cn(
        "border-b border-brown-50 last:border-0",
        !row.isActive && "opacity-60",
      )}>
        <td className="px-4 py-3 align-top">
          <span style={{ paddingLeft: indent }} className="inline-block">
            {row.depth > 0 && (
              <span className="mr-2 text-muted" aria-hidden>
                ↳
              </span>
            )}
            <span className="font-semibold text-brown-500">{row.name}</span>
          </span>
        </td>
        <td className="px-4 py-3 align-top">
          <code className="rounded bg-cream-100 px-1.5 py-0.5 text-xs text-brown-400">
            {row.slug}
          </code>
        </td>
        <td className="px-4 py-3 align-top text-brown-400">{row.sortOrder}</td>
        <td className="px-4 py-3 align-top text-brown-400">{row.productCount}</td>
        <td className="px-4 py-3 align-top">
          <StatusPill tone={row.isActive ? "active" : "closed"}>
            {row.isActive ? "Active" : "Inactive"}
          </StatusPill>
        </td>
        <td className="px-4 py-3 align-top">
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onAddChild}
              className="text-xs font-semibold text-brown-400 hover:text-rust-500"
            >
              Add subcategory
            </button>
            <span className="text-muted">·</span>
            <button
              type="button"
              onClick={onEdit}
              className="text-xs font-semibold text-brown-400 hover:text-rust-500"
            >
              Edit
            </button>
          </div>
        </td>
      </tr>
      {isAddingChild && (
        <tr className="border-b border-brown-50 last:border-0">
          <td colSpan={6} className="bg-peach-50/40 p-4">
            <CategoryForm
              mode="create"
              parentId={row.id}
              parentName={row.name}
              onCancel={onCancelAddChild}
              onSaved={onChildSaved}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Inline form (create + edit) ──────────────────────────────────────────

interface CategoryFormProps {
  mode: "create" | "edit";
  /** create-mode only — the parent the new category nests under (null = root). */
  parentId?: string | null;
  parentName?: string;
  /** edit-mode only — the row being edited. */
  row?: CategoryAdminRow;
  onCancel: () => void;
  onSaved: () => void;
}

function CategoryForm(props: CategoryFormProps) {
  const { mode, parentId = null, parentName, row, onCancel, onSaved } = props;
  const [name, setName] = useState(row?.name ?? "");
  const [slug, setSlug] = useState(row?.slug ?? "");
  const [sortOrder, setSortOrder] = useState(String(row?.sortOrder ?? 100));
  const [isActive, setIsActive] = useState(row?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const create = useMutation({
    mutationFn: async () =>
      api<{ category: CategoryAdminRow }>("/admin/categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug: slug.trim(),
          name: name.trim(),
          parent_id: parentId,
          sort_order: Number.parseInt(sortOrder, 10),
        }),
      }),
    onSuccess: () => onSaved(),
    onError: (err) => setError(extractError(err)),
  });

  const update = useMutation({
    mutationFn: async () => {
      if (!row) throw new Error("update without row");
      const body: Record<string, unknown> = {};
      if (name.trim() !== row.name) body.name = name.trim();
      const sortNum = Number.parseInt(sortOrder, 10);
      if (sortNum !== row.sortOrder) body.sort_order = sortNum;
      if (isActive !== row.isActive) body.is_active = isActive;
      if (Object.keys(body).length === 0) {
        return { category: row };
      }
      return api<{ category: CategoryAdminRow }>(
        `/admin/categories/${row.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
    },
    onSuccess: () => onSaved(),
    onError: (err) => setError(extractError(err)),
  });

  const submitting = create.isPending || update.isPending;

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    if (mode === "create") {
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim())) {
        setError(
          "Slug must be lowercase alphanumeric segments separated by single hyphens (e.g. 't-shirts').",
        );
        return;
      }
      if (name.trim().length === 0) {
        setError("Name is required.");
        return;
      }
      create.mutate();
    } else {
      if (name.trim().length === 0) {
        setError("Name cannot be blank.");
        return;
      }
      update.mutate();
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-peach-300 bg-peach-50/30 p-4"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-brown-500">
          {mode === "create"
            ? parentName
              ? `New category under "${parentName}"`
              : "New root category"
            : `Edit "${row?.name}"`}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="text-brown-300 hover:text-brown-500"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <FormField label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="T-Shirts"
            maxLength={120}
            disabled={submitting}
            autoFocus
          />
        </FormField>
        {mode === "create" ? (
          <FormField label="Slug (URL-safe, immutable)">
            <Input
              value={slug}
              onChange={(e) =>
                setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-"))
              }
              placeholder="t-shirts"
              maxLength={80}
              disabled={submitting}
            />
          </FormField>
        ) : (
          <FormField label="Slug (immutable)">
            <code className="block rounded-md border border-brown-100 bg-cream-50/50 px-3 py-2 text-sm text-brown-400">
              {row?.slug}
            </code>
          </FormField>
        )}
        <FormField label="Sort order">
          <Input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            min={0}
            max={10000}
            disabled={submitting}
          />
        </FormField>
        {mode === "edit" && (
          <FormField label="Status">
            <label className="flex items-center gap-2 text-sm text-brown-500">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={submitting}
                className="h-4 w-4 rounded border-brown-200 text-ink underline underline-offset-4 focus:ring-peach-300"
              />
              Active (deselect to deactivate)
            </label>
          </FormField>
        )}
      </div>

      {error && (
        <p className="mt-3 text-sm text-rust-600">{error}</p>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting
            ? "Saving…"
            : mode === "create"
              ? "Create category"
              : "Save changes"}
        </Button>
      </div>
    </form>
  );
}

// ─── Small bits ───────────────────────────────────────────────────────────

function SectionTitle(props: { eyebrow: string; title: string }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        {props.eyebrow}
      </p>
      <h2 className="mt-1 font-display text-2xl font-bold text-brown-500">
        {props.title}
      </h2>
    </div>
  );
}

function FormField(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        {props.label}
      </span>
      {props.children}
    </label>
  );
}

function extractError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `Request failed (${err.status}).`;
  }
  return "Save failed. Try again or check your connection.";
}
